/**
 * Anatomical joint rotation constraints for mannequin bones.
 *
 * Uses the industry-standard per-axis min/max approach (as in Blender, Maya,
 * Unreal): the delta quaternion from rest pose is decomposed into euler angles
 * around the bone-local axes (X, Y, Z) and each axis is independently clamped
 * to its own [min, max] range.
 *
 * ARP rig bone-local axis convention:
 *   Y = along the bone (twist axis)
 *   Z = flexion / extension
 *   X = lateral abduction / adduction
 *
 * For hinge joints, rotation is only permitted around a single axis within
 * a min/max range.
 */

import * as THREE from "three/webgpu";
import { FINGER_SPECS } from "./fingerData.ts";
import { decomposeSwingTwist, normalizeAngle } from "./quaternionMath.ts";

// ---------------------------------------------------------------------------
// Constraint types
// ---------------------------------------------------------------------------

interface HingeConstraint {
  type: "hinge";
  /** Local axis of rotation (unit vector) */
  axis: "x" | "y" | "z";
  /** Minimum angle in radians */
  minAngle: number;
  /** Maximum angle in radians */
  maxAngle: number;
}

/**
 * Per-axis rotation limits — the industry-standard approach.
 *
 * The delta quaternion from rest pose is decomposed into intrinsic ZYX euler
 * angles (flexion first, then twist, then lateral), and each component is
 * independently clamped to [min, max].
 *
 * ZYX order places twist (Y) as the middle axis. Since twist limits are
 * always ≤90° in this rig, gimbal lock (which occurs at middle axis = ±90°)
 * is avoided. Flexion (Z) and lateral (X) can safely exceed 90°.
 */
interface PerAxisConstraint {
  type: "per_axis";
  /** Rotation around bone-local X axis (lateral abduction / adduction) */
  xMin: number;
  xMax: number;
  /** Rotation around bone-local Y axis (twist / axial rotation) */
  yMin: number;
  yMax: number;
  /** Rotation around bone-local Z axis (flexion / extension) */
  zMin: number;
  zMax: number;
}

export type JointConstraint = HingeConstraint | PerAxisConstraint;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEG = Math.PI / 180;

function deg(d: number): number {
  return d * DEG;
}

/**
 * Shorthand to create a symmetric per-axis constraint (limits are ±value for
 * each axis). Angles in radians.
 */
function perAxis(
  xRange: number | [number, number],
  yRange: number | [number, number],
  zRange: number | [number, number],
): PerAxisConstraint {
  const [xMin, xMax] = typeof xRange === "number" ? [-xRange, xRange] : xRange;
  const [yMin, yMax] = typeof yRange === "number" ? [-yRange, yRange] : yRange;
  const [zMin, zMax] = typeof zRange === "number" ? [-zRange, zRange] : zRange;
  return { type: "per_axis", xMin, xMax, yMin, yMax, zMin, zMax };
}

// ---------------------------------------------------------------------------
// Constraint definitions by bone name
// ---------------------------------------------------------------------------

const CONSTRAINTS: Record<string, JointConstraint> = {};

// Helper to register symmetric L/R constraints.
// For per_axis constraints, the Z and Y axes are mirrored between L and R
// (min/max swapped and negated) because the bone-local frame is mirrored.
function addLR(base: string, c: JointConstraint) {
  CONSTRAINTS[base + "L"] = c;
  if (c.type === "per_axis") {
    CONSTRAINTS[base + "R"] = {
      ...c,
      zMin: -c.zMax,
      zMax: -c.zMin,
      yMin: -c.yMax,
      yMax: -c.yMin,
    };
  } else {
    CONSTRAINTS[base + "R"] = c;
  }
}

// ---------------------------------------------------------------------------
// Joint definitions — per-axis limits
//
// All limits target ballet/gymnast-level flexibility for artistic posing.
// Axis convention: X = lateral, Y = twist (along bone), Z = flexion/extension.
// ---------------------------------------------------------------------------

// Thighs (hip joint)
// Flexion (Z): ~95°. Extension: ~110°.
// Lateral (X): abduction ~100°. Adduction: ~30°.
// Twist (Y): internal/external rotation ~60° each.
addLR(
  "thigh",
  perAxis(
    [-deg(30), deg(100)], // X: adduction (-) / abduction (+)
    deg(60), // Y: twist ±60°
    [-deg(110), deg(95)], // Z: extension (-) / flexion (+)
  ),
);

// Shins (knees): hinge on Z axis (mirrored for L/R).
// Full flexion ~145°. Small negative range for hyperextension.
CONSTRAINTS.shinL = {
  type: "hinge",
  axis: "z",
  minAngle: deg(-10),
  maxAngle: deg(145),
};
CONSTRAINTS.shinR = {
  type: "hinge",
  axis: "z",
  minAngle: deg(-145),
  maxAngle: deg(10),
};

// Feet (ankle joint)
// Plantarflexion (Z+): dancers en pointe ~70°. Dorsiflexion (Z-): deep plié ~35°.
// Inversion/eversion (X): ~30° for artistic foot rolls.
// Twist (Y): minimal, ~15°.
addLR(
  "foot",
  perAxis(
    deg(30), // X: inversion/eversion ±30°
    deg(15), // Y: twist ±15°
    [-deg(35), deg(70)], // Z: dorsiflexion (-) / plantarflexion (+)
  ),
);

// Toes: hinge on X axis (flex/extend only)
addLR("toe", {
  type: "hinge",
  axis: "x",
  minAngle: deg(-45),
  maxAngle: deg(90),
});

// Upper arms (glenohumeral joint only — no scapula rhythm)
// Forward flexion (Z+): ~120° glenohumeral only (170° includes scapula).
// Extension (Z-): ~60° (80° includes scapula).
// Lateral abduction (X+): ~105° glenohumeral (150° includes scapula).
// Adduction (X-): ~30° across body.
// Twist (Y): internal/external rotation ~90° each.
addLR(
  "upper_arm",
  perAxis(
    [-deg(30), deg(105)], // X: adduction (-) / abduction (+)
    deg(90), // Y: twist ±90°
    [-deg(90), deg(120)], // Z: extension (-) / flexion (+)
  ),
);

// Forearms (elbows): hinge on Z axis, mirrored like knees.
CONSTRAINTS.forearmL = {
  type: "hinge",
  axis: "z",
  minAngle: deg(-105),
  maxAngle: deg(50),
};
CONSTRAINTS.forearmR = {
  type: "hinge",
  axis: "z",
  minAngle: deg(-50),
  maxAngle: deg(105),
};

// Hands (wrist joint)
// Flexion/extension (Z): ~80° each direction.
// Radial/ulnar deviation (X): ~30° each.
// Pronation/supination (Y): ~85° each (mostly from forearm, but modeled here).
addLR(
  "hand",
  perAxis(
    [-deg(70), deg(90)], // X: ±70°/90°
    deg(85), // Y: pronation/supination ±85°
    [-deg(55), deg(45)], // Z: ±55°/45°
  ),
);

// Shoulders (clavicle / scapula elevation)
// Limited range — scapula glide adds range on top of upper_arm.
addLR(
  "shoulder",
  perAxis(
    deg(20), // X: ±20°
    deg(15), // Y: ±15°
    deg(25), // Z: ±25°
  ),
);

// Spine segments: 3 bones share the total range.
// Per segment: ~15° flexion/extension, ~20° lateral, ~25° twist.
// Combined: ~45° flex/ext, ~60° lateral, ~75° twist.
CONSTRAINTS.spine = perAxis([-deg(20), deg(30)], deg(25), [-deg(20), deg(15)]);
CONSTRAINTS.spine1 = perAxis([-deg(20), deg(30)], deg(25), [-deg(20), deg(15)]);
CONSTRAINTS.spine2 = perAxis(deg(20), deg(25), [-deg(20), deg(15)]);

// Neck: expressive head movement for dancers.
CONSTRAINTS.neck = perAxis([-deg(30), deg(40)], deg(40), [-deg(30), deg(35)]);

// Head: combined with neck gives full expressive range.
CONSTRAINTS.head = perAxis(deg(20), deg(25), [-deg(20), deg(20)]);

// Hips (pelvis as a unit — tilt + lumbar)
// Generous cone (~65°) allows dynamic poses; ellipsoidal budget prevents corners.
// Axial rotation (twist): ±35°.
CONSTRAINTS.hips = perAxis([-deg(40), deg(80)], deg(35), deg(65));

// ---------------------------------------------------------------------------
// Finger joints
// ---------------------------------------------------------------------------

// Three.js strips dots from glTF names: finger_index.L.1 → finger_indexL1
//
// MCP (joint 1): condyloid joint (2 DOF: flexion + abduction).
// Bone-local: X = flexion/extension, Z = abduction (splay), Y = along finger.
//
// PIP (joint 2) and DIP (joint 3): hinge joints, flex/extend around local X.
const FINGER_NAMES = ["index", "middle", "ring", "pinky"] as const;
for (const finger of FINGER_NAMES) {
  const s = FINGER_SPECS[finger];
  for (const side of ["L", "R"]) {
    CONSTRAINTS[`finger_${finger}${side}1`] = perAxis(
      [-deg(s.mcpFlex), deg(10)], // X: flexion (-) / extension (+)
      deg(20), // Y: twist (minimal)
      deg(s.abduction), // Z: abduction/adduction (splay)
    );
    CONSTRAINTS[`finger_${finger}${side}2`] = {
      type: "hinge",
      axis: "x",
      minAngle: deg(-110),
      maxAngle: deg(5),
    };
    CONSTRAINTS[`finger_${finger}${side}3`] = {
      type: "hinge",
      axis: "x",
      minAngle: deg(-90),
      maxAngle: deg(5),
    };
  }
}

// Thumb CMC (joint 1): saddle joint with complex motion.
{
  const s = FINGER_SPECS.thumb;
  for (const side of ["L", "R"]) {
    CONSTRAINTS[`finger_thumb${side}1`] = perAxis(
      [-deg(s.abduction), deg(20)], // X: flexion (-) / extension (+)
      deg(30), // Y: opposition pronation
      deg(45), // Z: abduction ±45°
    );
    CONSTRAINTS[`finger_thumb${side}2`] = {
      type: "hinge",
      axis: "x",
      minAngle: deg(-80),
      maxAngle: deg(10),
    };
    CONSTRAINTS[`finger_thumb${side}3`] = {
      type: "hinge",
      axis: "x",
      minAngle: deg(-70),
      maxAngle: deg(10),
    };
  }
}

// ---------------------------------------------------------------------------
// Scratch quaternions/vectors for constraint application (avoids allocations)
// ---------------------------------------------------------------------------

const _restQ = new THREE.Quaternion();
const _restInvQ = new THREE.Quaternion();
const _deltaQ = new THREE.Quaternion();
const _resultQ = new THREE.Quaternion();
const _deltaSnap = new THREE.Quaternion();

const _hingeAxis = new THREE.Vector3();
const _hingeSwing = new THREE.Quaternion();
const _hingeTwist = new THREE.Quaternion();

// Per-axis scratch
const _euler = new THREE.Euler();

// ---------------------------------------------------------------------------
// Constraint application
// ---------------------------------------------------------------------------

/**
 * Clamp a quaternion to the anatomical constraint for the given bone name.
 * Constraints operate on the DELTA from rest pose, not the absolute quaternion.
 *
 * @param boneName - The bone name (dots stripped, matching GLB naming)
 * @param quaternion - The desired local rotation quaternion (absolute, will not be mutated)
 * @param restPose - The bone's rest-pose local quaternion [x,y,z,w] (from GLB bind pose)
 * @returns A new quaternion clamped to the bone's constraint limits
 */
export function applyConstraint(
  boneName: string,
  quaternion: THREE.Quaternion,
  restPose?: [number, number, number, number],
  weight: number = 1,
  activeAxis?: "x" | "y" | "z" | null,
): THREE.Quaternion {
  const constraint = CONSTRAINT_OVERRIDES[boneName] ?? CONSTRAINTS[boneName];
  if (!constraint) return quaternion.clone();
  if (weight <= 0) return quaternion.clone();

  // Compute delta from rest pose: delta = inv(rest) * current
  if (restPose) {
    _restQ.set(restPose[0], restPose[1], restPose[2], restPose[3]);
  } else {
    _restQ.identity();
  }
  _restInvQ.copy(_restQ).invert();
  _deltaQ.copy(_restInvQ).multiply(quaternion);
  _deltaQ.normalize();

  _deltaSnap.copy(_deltaQ);

  // Apply constraint to the delta
  let constrainedDelta: THREE.Quaternion;
  if (constraint.type === "hinge") {
    constrainedDelta = applyHingeConstraint(_deltaSnap, constraint);
  } else if (constraint.type === "per_axis") {
    constrainedDelta = applyPerAxisConstraint(
      _deltaSnap,
      constraint,
      activeAxis,
    );
  } else {
    constrainedDelta = new THREE.Quaternion(); // identity fallback
  }

  // Reconstruct: constrained = rest * constrained_delta
  const fullyConstrained = _resultQ
    .copy(_restQ)
    .multiply(constrainedDelta)
    .clone();

  // Blend between unconstrained and constrained based on weight
  if (weight >= 1) return fullyConstrained;
  return quaternion.clone().slerp(fullyConstrained, weight);
}

function applyHingeConstraint(
  q: THREE.Quaternion,
  c: HingeConstraint,
): THREE.Quaternion {
  _hingeAxis.set(
    c.axis === "x" ? 1 : 0,
    c.axis === "y" ? 1 : 0,
    c.axis === "z" ? 1 : 0,
  );

  decomposeSwingTwist(q, _hingeAxis, _hingeSwing, _hingeTwist);

  let twistAngle = normalizeAngle(
    2 *
      Math.atan2(
        _hingeTwist.x * _hingeAxis.x +
          _hingeTwist.y * _hingeAxis.y +
          _hingeTwist.z * _hingeAxis.z,
        _hingeTwist.w,
      ),
  );

  twistAngle = Math.max(c.minAngle, Math.min(c.maxAngle, twistAngle));

  return new THREE.Quaternion().setFromAxisAngle(_hingeAxis, twistAngle);
}

/**
 * Per-axis constraint: decompose delta into ZYX intrinsic euler angles,
 * clamp each, then enforce an ellipsoidal rotation budget.
 *
 * ZYX order places Y (twist) as the middle axis. Since twist limits
 * never exceed 90° in this rig, gimbal lock (middle axis ±90°) is
 * avoided. Z (flexion) and X (lateral) can safely exceed 90°.
 *
 * The ellipsoidal budget prevents anatomically impossible poses where
 * multiple axes are simultaneously at their individual maximums. For
 * each axis, the "utilization" is the fraction of whichever limit (min
 * or max) the current angle is closest to. The sum of squares of all
 * three utilizations must be <= 1:
 *
 *   (x/xLim)² + (y/yLim)² + (z/zLim)² <= 1
 *
 * When exceeded, all three are scaled proportionally back to the
 * ellipsoid surface, preserving the direction of motion.
 */
function applyPerAxisConstraint(
  q: THREE.Quaternion,
  c: PerAxisConstraint,
  activeAxis?: "x" | "y" | "z" | null,
): THREE.Quaternion {
  // Decompose delta quaternion into euler angles (YZX intrinsic order)
  _euler.setFromQuaternion(q, "ZYX");

  // Clamp each axis independently first
  _euler.x = Math.max(c.xMin, Math.min(c.xMax, _euler.x));
  _euler.y = Math.max(c.yMin, Math.min(c.yMax, _euler.y));
  _euler.z = Math.max(c.zMin, Math.min(c.zMax, _euler.z));

  // Ellipsoidal budget: prevents impossible combined rotations.
  // Skipped when the user is driving a single axis (activeAxis) — in that
  // case the per-axis clamp above is sufficient and the budget would cause
  // cross-axis bleed by scaling down axes the user isn't touching.
  if (!activeAxis) {
    const xLim = _euler.x >= 0 ? c.xMax : -c.xMin;
    const yLim = _euler.y >= 0 ? c.yMax : -c.yMin;
    const zLim = _euler.z >= 0 ? c.zMax : -c.zMin;

    const xn = xLim > 1e-10 ? Math.abs(_euler.x) / xLim : 0;
    const yn = yLim > 1e-10 ? Math.abs(_euler.y) / yLim : 0;
    const zn = zLim > 1e-10 ? Math.abs(_euler.z) / zLim : 0;

    const budget = xn * xn + yn * yn + zn * zn;

    if (budget > 1) {
      const scale = 1 / Math.sqrt(budget);
      _euler.x = Math.sign(_euler.x) * xn * scale * xLim;
      _euler.y = Math.sign(_euler.y) * yn * scale * yLim;
      _euler.z = Math.sign(_euler.z) * zn * scale * zLim;
    }
  }

  return new THREE.Quaternion().setFromEuler(_euler);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Check whether a constraint is defined for the given bone name.
 */
export function hasConstraint(boneName: string): boolean {
  return boneName in CONSTRAINTS || boneName in CONSTRAINT_OVERRIDES;
}

/**
 * Look up the constraint definition for a bone (read-only).
 * Runtime overrides take precedence over built-in definitions.
 */
export function getConstraint(
  boneName: string,
): Readonly<JointConstraint> | undefined {
  return CONSTRAINT_OVERRIDES[boneName] ?? CONSTRAINTS[boneName];
}

/**
 * Get the built-in (default) constraint for a bone, ignoring overrides.
 */
export function getDefaultConstraint(
  boneName: string,
): Readonly<JointConstraint> | undefined {
  return CONSTRAINTS[boneName];
}

// ---------------------------------------------------------------------------
// Runtime constraint overrides
// ---------------------------------------------------------------------------

const CONSTRAINT_OVERRIDES: Record<string, JointConstraint> = {};

export function setConstraintOverride(
  boneName: string,
  constraint: JointConstraint,
): void {
  CONSTRAINT_OVERRIDES[boneName] = constraint;
}

export function clearConstraintOverride(boneName: string): void {
  delete CONSTRAINT_OVERRIDES[boneName];
}

export function clearAllConstraintOverrides(): void {
  for (const k of Object.keys(CONSTRAINT_OVERRIDES))
    delete CONSTRAINT_OVERRIDES[k];
}

export function getAllConstraintOverrides(): Readonly<
  Record<string, JointConstraint>
> {
  return CONSTRAINT_OVERRIDES;
}

export function loadConstraintOverrides(
  overrides: Record<string, JointConstraint>,
): void {
  for (const k of Object.keys(CONSTRAINT_OVERRIDES))
    delete CONSTRAINT_OVERRIDES[k];
  Object.assign(CONSTRAINT_OVERRIDES, overrides);
}

export function getAllConstrainedBones(): string[] {
  const names = new Set([
    ...Object.keys(CONSTRAINTS),
    ...Object.keys(CONSTRAINT_OVERRIDES),
  ]);
  return [...names].sort();
}
